import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { TicketStatusBadge, PriorityBadge, IntentBadge, SentimentBadge, ConfidenceBadge } from './TicketStatusBadge'
import { formatDateTime } from '@/lib/utils'
import { TicketProductBadge } from './TicketProductBadge'
import type { SupportTicket } from '@/types/entities'

interface TicketTableProps {
  tickets: SupportTicket[]
  onViewTicket: (ticket: SupportTicket) => void
  isLoading?: boolean
}

export function TicketTable({ tickets, onViewTicket, isLoading }: TicketTableProps) {
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  if (tickets.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No tickets found.
      </div>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[280px]">Subject</TableHead>
          <TableHead>Customer</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Product</TableHead>
          <TableHead>Priority</TableHead>
          <TableHead>Intent</TableHead>
          <TableHead>Sentiment</TableHead>
          <TableHead>Confidence</TableHead>
          <TableHead className="text-right">Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tickets.map((ticket) => (
          <TableRow
            key={ticket.id}
            className="cursor-pointer hover:bg-muted/50"
            onClick={() => onViewTicket(ticket)}
          >
            <TableCell className="font-medium max-w-[280px] truncate">
              {ticket.subject}
            </TableCell>
            <TableCell className="text-sm text-muted-foreground">
              <div className="truncate max-w-[160px]">
                {ticket.contactName || ticket.contactEmail || '—'}
              </div>
            </TableCell>
            <TableCell>
              <TicketStatusBadge status={ticket.status} />
            </TableCell>
            <TableCell>
              <TicketProductBadge ticket={ticket} />
            </TableCell>
            <TableCell>
              <PriorityBadge priority={ticket.priority} />
            </TableCell>
            <TableCell>
              <IntentBadge intent={ticket.intent} />
            </TableCell>
            <TableCell>
              <SentimentBadge sentiment={ticket.sentiment} />
            </TableCell>
            <TableCell>
              <ConfidenceBadge confidence={ticket.confidence} />
            </TableCell>
            <TableCell className="text-right text-sm text-muted-foreground whitespace-nowrap">
              {formatDateTime(ticket.createdAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
